// Authenticator enrollment — public page reached via a single-use link
// (/enroll/<token>) printed by scripts/create_user.py --enroll. Shows the QR
// code for any TOTP app; confirming the first 6-digit code activates
// code-based login and consumes the token. Ported from cinematic_database,
// restyled in the Epicure voice.

import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";

import { ApiError, apiFetch, post } from "@/api/client";
import { cn } from "@/lib/utils";

interface Pending {
  username: string;
  otpauth_uri: string;
  qr_svg: string;
  secret: string;
}

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "pending"; data: Pending }
  | { kind: "done"; username: string };

export default function Enroll() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Pending>(`/api/enroll/${token}`)
      .then((data) => !cancelled && setState({ kind: "pending", data }))
      .catch(() => !cancelled && setState({ kind: "invalid" }));
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (busy || state.kind !== "pending") return;
    setBusy(true);
    setError(null);
    try {
      await post(`/api/enroll/${token}`, { code });
      setState({ kind: "done", username: state.data.username });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts — wait a moment."
          : "Wrong code — wait for the next one and try again.",
      );
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-8 overflow-y-auto bg-background p-6">
      <h1 className="text-center font-display text-6xl leading-[0.95] font-medium tracking-[-0.02em] text-foreground">
        <span className="block pb-1 italic text-brand-ink">yes,</span>
        Chef
      </h1>

      <div className="w-full max-w-sm rounded-[var(--radius-card)] border border-line-strong bg-surface p-6 shadow-[0_15px_40px_rgba(0,0,0,0.2)]">
        {state.kind === "loading" && <p className="text-sm text-dim">loading…</p>}

        {state.kind === "invalid" && (
          <p className="text-sm text-dim">
            This enrollment link is invalid or has expired. Mint a fresh one with{" "}
            <code className="font-mono text-xs">create_user.py --enroll</code>.
          </p>
        )}

        {state.kind === "pending" && (
          <>
            <p className="text-sm leading-relaxed text-dim">
              Set up code login for <b className="text-foreground">{state.data.username}</b>:
              scan this QR with your authenticator app (add account → other), then enter the
              6-digit code it shows.
            </p>
            <div
              className="mx-auto mt-4 w-56 rounded-xl bg-white p-3 [&_svg]:h-auto [&_svg]:w-full"
              // Server-generated SVG from the otpauth URI — no external content.
              dangerouslySetInnerHTML={{ __html: state.data.qr_svg }}
            />
            <details className="mt-3 text-xs text-dim">
              <summary className="cursor-pointer">can't scan? enter the key manually</summary>
              <code className="mt-1 block font-mono break-all">{state.data.secret}</code>
            </details>
            <form onSubmit={confirm} className="mt-4 flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit code"
                disabled={busy}
                autoFocus
                className="min-w-0 flex-1 rounded-lg border border-line-strong bg-transparent px-3 py-2.5 font-mono text-sm outline-none focus:border-brand-ink"
              />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className={cn(
                  "cursor-pointer rounded-full bg-brand px-5 text-sm font-bold text-primary-foreground",
                  "transition-[transform,background-color,opacity] duration-500 ease-epicure",
                  "hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-[0.98]",
                  "disabled:cursor-default disabled:opacity-35 disabled:hover:translate-y-0",
                )}
              >
                →
              </button>
            </form>
            {error && <p className="mt-2 text-xs text-status-error">{error}</p>}
          </>
        )}

        {state.kind === "done" && (
          <>
            <p className="text-sm leading-relaxed">
              ✓ Authenticator active for <b>{state.username}</b>. From now on, log in with the
              current 6-digit code instead of your password.
            </p>
            <a
              href="/"
              className="mt-4 inline-block cursor-pointer rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-primary-foreground"
            >
              go to login →
            </a>
          </>
        )}
      </div>
    </div>
  );
}
